import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HomePostLoginDashboard from "@/components/auth/HomePostLoginDashboard";
import InstagramRedazioneStrip from "@/components/InstagramRedazioneStrip";
import SEOHead from "@/components/SEOHead";
import SwipeableCards from "@/components/SwipeableCards";
import {
  schemaOrganization,
  schemaWebsite,
  schemaBreadcrumb,
  schemaFaq,
  combineSchemas,
} from "@/lib/siteSchemas";
import TeamTimeline from "@/components/TeamTimeline";
import TeamAvatar from "@/components/TeamAvatar";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import GoogleAggregateRatingSchema from "@/components/GoogleAggregateRatingSchema";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Car, Home, Heart, Building2, ArrowRight, Shield, Users,
  ClipboardCheck, FileUp, MessageSquare, CheckCircle2,
  Phone, UserCheck, Lock, TrendingUp, Handshake, Stethoscope, Plane, CarFront, Briefcase, Calculator, Compass,
  ShieldCheck, Zap, Clock, AlertTriangle, ArrowUpRight, Sparkles, Star, Send, Newspaper, Instagram, Flame
} from "lucide-react";
import DroneIcon from "@/components/DroneIcon";
// import CompagniePartnerSection from "@/components/sections/CompagniePartnerSection";  // FASE 8.4: spostato su /compagnie
import EventoBanner from "@/components/EventoBanner";
import EventsTeaserSection from "@/components/sections/EventsTeaserSection";
import HomeEventsSection from "@/components/sections/HomeEventsSection";
import axios from "axios";

const fade = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const PRODUCTS = [
  { icon: Car, title: "Auto", desc: "RC Auto, Furto, Kasko, Cristalli", link: "/preventivo-auto", span: "md:col-span-2" },
  { icon: Stethoscope, title: "Professioni Sanitarie", desc: "RC adeguata Decreto Gelli", link: "/questionario/professionisanitarie", span: "md:col-span-2", hot: true },
  { icon: Building2, title: "Aziende", desc: "CAT NAT, RC, D&O, Cyber Risk, Fleet", link: "/aziende", span: "md:col-span-2" },
  { icon: Briefcase, title: "Professionisti", desc: "RC Professionale obbligatoria", link: "/professioni", span: "md:col-span-2" },
  { icon: Users, title: "Famiglia", desc: "Casa, Vita, Infortuni, Salute", link: "/famiglia", span: "md:col-span-2" },
  { icon: Plane, title: "Viaggi", desc: "Copertura completa worldwide", link: "/contatti?prodotto=viaggio", span: "md:col-span-2" },
  { icon: CarFront, title: "Noleggio", desc: "NLT con Ayvens e Reale Mutua", link: "/noleggio", span: "md:col-span-3" },
  { icon: Compass, title: "Scopri le Polizze", desc: "Trova la copertura perfetta per te", link: "/scopri-polizze", span: "md:col-span-3", highlight: true },
];

const STEPS = [
  { num: "01", icon: ClipboardCheck, title: "Analizza", desc: "Compila il questionario intelligente. 3 minuti per capire di cosa hai davvero bisogno." },
  { num: "02", icon: MessageSquare, title: "Confronta", desc: "Un consulente dedicato confronta le migliori soluzioni dalle compagnie top del mercato." },
  { num: "03", icon: ShieldCheck, title: "Proteggi", desc: "Firma digitale e attivazione immediata. La tua protezione parte oggi." },
];

const STATS = [
  { value: "40+", label: "Anni di Esperienza", icon: TrendingUp },
  { value: "10K+", label: "Clienti Protetti", icon: Users },
  { value: "24h", label: "Tempo di Risposta", icon: Clock },
];

const DIFFS = [
  { icon: Handshake, title: "Nessun algoritmo", desc: "Un essere umano analizza la tua situazione. Non un bot, non un comparatore." },
  { icon: UserCheck, title: "Consulente dedicato", desc: "Una persona che conosce la tua storia, anticipa i tuoi bisogni, risponde al telefono." },
  { icon: Lock, title: "Zero compromessi sulla privacy", desc: "Nessun tracciamento, nessun dato venduto. Solo cookie tecnici per l'admin. GDPR al 100%." },
  { icon: TrendingUp, title: "Dal 1982, senza interruzioni", desc: "Tre generazioni di famiglie ci affidano la loro protezione. La nostra esperienza si vede." },
];

const FAQS = [
  { q: "In cosa siete diversi dai comparatori online?", a: "I siti di comparazione ti mostrano il prezzo piu' basso. Noi ti mostriamo la copertura migliore. Il prezzo piu' basso spesso nasconde esclusioni che scopri solo al momento del sinistro. Noi leggiamo le clausole per te e ti spieghiamo cosa stai davvero comprando." },
  { q: "Quanto costa la consulenza?", a: "Il preventivo e' sempre gratuito e senza impegno. La valutazione approfondita delle tue esigenze - analisi del profilo di rischio, raccomandazioni personalizzate e report scritto - e' un servizio professionale con pacchetti a partire da 100 euro. Se non sei soddisfatto, rimborso totale entro 7 giorni." },
  { q: "Come funziona il processo?", a: "Compili un questionario online (3 minuti), carichi eventuali documenti, e entro 24 ore un consulente ti chiama con una proposta personalizzata. Nessun obbligo." },
  { q: "I miei dati sono al sicuro?", a: "Assolutamente. Zero cookie di tracciamento, zero analytics, zero pixel. I documenti sono crittografati. Siamo GDPR compliant al 100%. Puoi verificarlo nella nostra Cookie Policy." },
  { q: "Lavorate con tutte le compagnie?", a: "Collaboriamo con le principali compagnie italiane e internazionali: Reale Mutua, AIG, Lloyd's, Groupama, Net Insurance e molte altre. Questo ci permette di trovare sempre la soluzione migliore." },
  { q: "Avete convenzioni con grandi aziende?", a: "Si. Abbiamo accordi attivi con realta' di primo piano nei settori difesa &amp; aerospazio, automotive, public utilities, banking, motori aeronautici e gruppi industriali come Hitachi. Se sei dipendente di uno di questi gruppi puoi accedere a tariffe agevolate. Visita la pagina Convenzioni per dettagli." },
  { q: "Posso fare il noleggio lungo termine come privato?", a: "Si. Operiamo come sub-agente BlueAssistance con mandato Ayvens per il noleggio a lungo termine privati: accettiamo clienti con busta paga, pensionati e liberi professionisti. Le auto noleggiate tramite la nostra agenzia sono assicurate Reale Mutua, garantendo copertura completa e continuita' di servizio. Colucci Assicuratori segue tutto il processo end-to-end nelle 3 sedi Campania (tempi di consegna variabili in funzione del veicolo e del partner produttivo)." },
  { q: "Avete sede a Portici, Napoli e Pomigliano?", a: "Si, abbiamo 3 sedi operative: Portici (Via Diaz 182/L), Napoli (Via Giordano Bruno 169) e Pomigliano d'Arco (Via Carducci 6). Puoi venire in agenzia o richiedere consulenza online via video call." },
  { q: "Cosa fare in caso di sinistro?", a: "Hai 3 modi per aprire un sinistro: telefono 081 761 1000 (preferito), email a sinistri@colucciassicuratori.com, o tramite l'area clienti del nostro sito. Ti seguiamo per tutto il processo, dalla denuncia al rimborso, gratuitamente." },
];

export default function HomePage() {
  const [showReview, setShowReview] = useState(false);
  const [reviewData, setReviewData] = useState({ voto: 5, testo: "", servizio: "" });
  const [reviewSending, setReviewSending] = useState(false);
  const [reviewSent, setReviewSent] = useState(false);
  // TASK 7: homepage CMS config dinamico
  const [homepageCfg, setHomepageCfg] = useState(null);
  // v1.72.4 — Hero automatico: se ≥1 evento attivo mostra EventsTeaser, altrimenti banner 73%.
  // Default null (loading) → mostra 73% (fallback anti-flash).
  const [hasActiveEvents, setHasActiveEvents] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    axios.get(`${API}/api/public/homepage/config`)
      .then((r) => setHomepageCfg(r.data))
      .catch(() => setHomepageCfg({ hero_mode: "stats" })); // fallback safe
  }, [API]);

  // v1.72.4 — Rileva eventi attivi per switchare hero senza CMS toggle.
  // Response shape backend: {"items": [...], "total": N}
  useEffect(() => {
    axios.get(`${API}/api/public/eventi/in-programma?limit=1`)
      .then((r) => setHasActiveEvents(Array.isArray(r.data?.items) && r.data.items.length > 0))
      .catch(() => setHasActiveEvents(false));
  }, [API]);

  const submitReview = async () => {
    if (!reviewData.testo.trim()) return;
    setReviewSending(true);
    try {
      const resp = await fetch(`${API}/api/recensioni`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reviewData),
      });
      if (resp.ok) { setReviewSent(true); }
      else { alert("Errore nell'invio. Riprova."); }
    } catch { alert("Errore. Riprova."); }
    setReviewSending(false);
  };

  return (
    <>
      <SEOHead
        title="Colucci Assicuratori"
        description="Agenzia assicurativa indipendente iscritta IVASS a Portici (NA). RC Auto, Casa, Vita, Professionisti, Aziende, Noleggio Lungo Termine. Convenzioni con grandi gruppi industriali italiani. Consulenza personalizzata, mai automatizzata. Storica agenzia in Campania dal 1982."
        canonical="/"
        keywords={[
          "colucci assicuratori",
          "assicurazioni napoli",
          "consulenza assicurativa portici",
          "RC auto napoli",
          "polizza casa campania",
          "noleggio lungo termine ayvens",
          "agenzia assicurativa IVASS",
          "assicurazione professionisti",
        ]}
        jsonLd={combineSchemas(
          schemaOrganization(),
          schemaWebsite(),
          schemaBreadcrumb([{ name: "Home", path: "/" }]),
          schemaFaq(FAQS.map((f) => ({ q: f.q, a: f.a }))),
        )["@graph"].map((s) => ({ "@context": "https://schema.org", ...s }))}
      />
      <GoogleAggregateRatingSchema />
      {/* TASK 7.2-fix: EventoBanner sticky top rimosso su richiesta utente.
          Il componente resta disponibile in /components/EventoBanner.jsx per riuso futuro.
          La promozione eventi resta visibile nella hero <EventsTeaserSection /> piu' in basso. */}
      <Header />
      <main className="bg-[#FAFAFA]" data-testid="homepage-main">

        {/* ===== HERO ===== */}
        <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
          <div className="absolute inset-0 mesh-gradient" />
          <div className="absolute top-20 right-0 w-[600px] h-[600px] rounded-full bg-[#163321]/5 blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#D97B58]/5 blur-[100px]" />
          <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 lg:py-0 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div initial="hidden" animate="visible" variants={stagger}>
                {/* v1.72.4 — HERO automatico: eventi attivi ≥1 → teaser, altrimenti banner 73%.
                    Override manuale CMS: hero_mode="stats_forced" o "evento_forced" (backward-compat).
                    Fallback anti-flash: durante il fetch (hasActiveEvents === null) mostra STATS. */}
                {(() => {
                  const forceEvento = homepageCfg?.hero_mode === "evento_forced";
                  const forceStats  = homepageCfg?.hero_mode === "stats_forced";
                  const showEventi  = forceEvento || (!forceStats && hasActiveEvents === true);
                  return showEventi;
                })() ? (
                  <EventsTeaserSection config={homepageCfg} />
                ) : (
                  <>
                    <motion.div variants={fade} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-100 mb-8">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span className="text-xs font-bold text-red-600 uppercase tracking-wider">{homepageCfg?.hero_stats_eyebrow || "Dati IVASS 2025"}</span>
                    </motion.div>
                    <motion.h1 variants={fade} className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tighter text-zinc-900 leading-[0.95] mb-6">
                      {(homepageCfg?.hero_stats_title || "Il 73% degli italiani e'")} <span className="gradient-text">{homepageCfg?.hero_stats_highlight || "sottoassicurato."}</span>
                    </motion.h1>
                    <motion.p variants={fade} className="text-lg sm:text-xl text-zinc-500 leading-relaxed mb-10 max-w-lg">
                      {homepageCfg?.hero_stats_subtitle || "Non aspettare l'imprevisto per scoprire quanto vale la tua sicurezza."}
                      {" "}Ottieni la copertura che meriti in <strong className="text-zinc-700">3 minuti</strong>.
                    </motion.p>
                    <motion.div variants={fade} className="flex flex-col sm:flex-row gap-4">
                      {/* v1.72.4 — CTA renamed + scroll smooth alla sezione #prodotti (no redirect) */}
                      <button
                        type="button"
                        onClick={() => document.getElementById("prodotti")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                        className="inline-flex items-center justify-center gap-2 px-8 h-14 bg-[#163321] text-white text-sm font-semibold rounded-full hover:bg-[#1d4a2e] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-xl shadow-[#163321]/25"
                        data-testid="btn-hero-preventivo"
                      >
                        Effettua il tuo preventivo <ArrowRight className="w-4 h-4" />
                      </button>
                      <Link to="/consulenza" className="inline-flex items-center justify-center gap-2 px-8 h-14 bg-white text-zinc-900 text-sm font-semibold rounded-full border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 transition-all duration-200" data-testid="hero-consulenza-btn">
                        Prenota Consulenza
                      </Link>
                    </motion.div>
                  </>
                )}
                <motion.div variants={fade} className="mt-12 flex flex-wrap items-center gap-6 text-zinc-400">
                  {[
                    { icon: CheckCircle2, text: "Preventivo gratuito" },
                    { icon: CheckCircle2, text: "Risposta in 24h" },
                    { icon: CheckCircle2, text: "Iscritti IVASS" },
                  ].map((t, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <t.icon className="w-4 h-4 text-[#163321]" /><span>{t.text}</span>
                    </div>
                  ))}
                </motion.div>
              </motion.div>

              {/* Stats cards + Review */}
              <motion.div initial="hidden" animate="visible" variants={stagger} className="grid grid-cols-2 gap-4">
                {STATS.map((s, i) => (
                  <motion.div key={i} variants={fade}
                    className={i === 0
                      ? 'rounded-2xl p-6 bg-[#163321] text-white border border-[#163321] shadow-lg shadow-[#163321]/20'
                      : 'glass-card rounded-2xl p-6'}
                    data-testid={`stat-card-${i}`}>
                    <s.icon className={`w-5 h-5 mb-3 ${i === 0 ? 'text-white/60' : 'text-[#163321]'}`} />
                    <p className={`text-3xl font-heading font-bold stat-number ${i === 0 ? 'text-white' : 'text-zinc-900'}`}>{s.value}</p>
                    <p className={`text-xs mt-1 ${i === 0 ? 'text-white/70' : 'text-zinc-500'}`}>{s.label}</p>
                  </motion.div>
                ))}
                <motion.button variants={fade} onClick={() => { setShowReview(true); setReviewSent(false); setReviewData({ voto: 5, testo: "", servizio: "" }); }}
                  className="glass-card rounded-2xl p-6 text-left hover:-translate-y-1 hover:shadow-lg transition-all duration-300 border-[#D97B58]/20 bg-[#D97B58]/5 group cursor-pointer"
                  data-testid="review-card-btn">
                  <Star className="w-5 h-5 mb-3 text-[#D97B58] group-hover:scale-110 transition-transform" />
                  <p className="text-sm font-heading font-bold text-zinc-900">Lascia una<br />Recensione</p>
                  <p className="text-xs mt-1 text-zinc-500">Anonima e sicura</p>
                </motion.button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FASE 4.2 — Dashboard post-login (visibile solo se utente loggato) */}
        <HomePostLoginDashboard />

        {/* FASE 4.2.11 TASK 3 — Sezione Eventi restored (sotto hero, sopra prodotti) */}
        <HomeEventsSection />

        {/* ===== PRODUCTS BENTO GRID ===== */}
        <section id="prodotti" className="py-24 md:py-32" data-testid="products-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="text-center mb-16">
              <motion.p variants={fade} className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97B58] mb-4">I Nostri Prodotti</motion.p>
              <motion.h2 variants={fade} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 mb-4">
                Tutto quello che ti serve.<br className="hidden sm:block" /><span className="text-zinc-400">In un unico posto.</span>
              </motion.h2>
            </motion.div>

            {/* Mobile: swipeable cards con dots + hint */}
            <div className="md:hidden">
              <SwipeableCards testId="products-swipe" ariaLabel="Prodotti assicurativi" cardMinWidth={240} hintLabel="Scorri i prodotti">
                {PRODUCTS.map((p) => {
                  const Icon = p.icon === DroneIcon ? DroneIcon : p.icon;
                  return (
                    <Link key={p.title} to={p.link}
                      className={`group relative flex flex-col justify-between h-44 p-5 rounded-2xl border transition-all ${p.highlight ? 'bg-[#163321] text-white border-[#163321]' : p.hot ? 'bg-gradient-to-br from-[#D97B58]/10 to-white border-[#D97B58]/25' : 'bg-white border-zinc-200'}`}
                      data-testid={`product-mobile-${p.title.toLowerCase().replace(/\s/g, '-')}`}>
                      <div>
                        {p.hot && <span className="absolute top-3 right-3 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#D97B58] text-white rounded-full">Top</span>}
                        <Icon className={`w-7 h-7 mb-3 ${p.highlight ? 'text-white/80' : p.hot ? 'text-[#D97B58]' : 'text-[#163321]'}`} />
                        <h3 className={`font-heading text-base font-semibold mb-1 ${p.highlight ? '' : 'text-zinc-900'}`}>{p.title}</h3>
                        <p className={`text-xs leading-relaxed ${p.highlight ? 'text-white/70' : 'text-zinc-500'}`}>{p.desc}</p>
                      </div>
                      <ArrowUpRight className={`w-4 h-4 mt-3 ${p.highlight ? 'text-white/50' : 'text-zinc-400'}`} />
                    </Link>
                  );
                })}
              </SwipeableCards>
            </div>

            {/* Desktop: bento grid 6 colonne */}
            <div className="hidden md:grid grid-cols-6 gap-4">
              {PRODUCTS.map((p) => {
                const Icon = p.icon === DroneIcon ? DroneIcon : p.icon;
                return (
                  <Link key={p.title} to={p.link}
                    className={`group relative ${p.span} p-6 rounded-2xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${p.highlight ? 'bg-[#163321] text-white border-[#163321] hover:shadow-[#163321]/20' : p.hot ? 'bg-gradient-to-br from-[#D97B58]/5 to-transparent border-[#D97B58]/20 hover:border-[#D97B58]/40' : 'bg-white border-zinc-200/80 hover:border-zinc-300'}`}
                    data-testid={`product-${p.title.toLowerCase().replace(/\s/g, '-')}`}>
                    {p.hot && <span className="absolute top-3 right-3 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#D97B58] text-white rounded-full">Top</span>}
                    <Icon className={`w-6 h-6 mb-4 ${p.highlight ? 'text-white/70' : p.hot ? 'text-[#D97B58]' : 'text-[#163321]'}`} />
                    <h3 className={`font-heading text-base font-semibold mb-1 ${p.highlight ? '' : 'text-zinc-900'}`}>{p.title}</h3>
                    <p className={`text-xs leading-relaxed ${p.highlight ? 'text-white/60' : 'text-zinc-500'}`}>{p.desc}</p>
                    <ArrowUpRight className={`w-4 h-4 mt-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${p.highlight ? 'text-white/40' : 'text-zinc-300'}`} />
                  </Link>
                );
              })}
            </div>

            {/* Quick CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
              <Link to="/questionario/professionisanitarie" className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-[#D97B58]/10 border border-[#D97B58]/20 hover:bg-[#D97B58]/15 transition-all group" data-testid="hero-sanitario-btn">
                <Stethoscope className="w-5 h-5 text-[#D97B58]" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-zinc-900">RC Professioni Sanitarie</p>
                  <p className="text-xs text-zinc-500">Adeguate al Decreto Gelli</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#D97B58] group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/quotatore" className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-[#163321]/5 border border-[#163321]/10 hover:bg-[#163321]/10 transition-all group" data-testid="hero-quotatore-btn">
                <Calculator className="w-5 h-5 text-[#163321]" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-zinc-900">Quotatore Polizza Vita</p>
                  <p className="text-xs text-zinc-500">Calcola il capitale assicurato</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#163321] group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Notizie Assicurative Banner */}
            <div className="mt-5" data-testid="notizie-banner-section">
              <Link to="/notizie-assicurative"
                className="group relative block overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-r from-zinc-900 via-[#163321] to-zinc-900 hover:shadow-2xl hover:shadow-[#163321]/30 transition-all duration-300" data-testid="notizie-banner-btn">
                <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-[#D97B58]/20 blur-[100px]" />
                <div className="relative flex flex-col sm:flex-row items-center gap-5 p-5 sm:p-7">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center shrink-0 border border-white/20 group-hover:scale-105 transition-transform">
                    <Newspaper className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <div className="flex items-center gap-2 justify-center sm:justify-start mb-1 flex-wrap">
                      <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#D97B58] text-white rounded-full">Oggi</span>
                      <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-white/15 text-white rounded-full border border-white/20">AI Powered</span>
                    </div>
                    <h3 className="font-heading text-lg sm:text-xl font-bold text-white mb-1">Notizie Assicurative del Giorno</h3>
                    <p className="text-sm text-white/70 leading-relaxed">
                      Le notizie italiane di oggi lette da un consulente esperto: cosa cambia per te, quali rischi emergono, quali coperture valutare.
                    </p>
                  </div>
                  <div className="shrink-0 flex items-center gap-2 px-5 py-2.5 bg-white text-[#163321] text-sm font-semibold rounded-full group-hover:bg-zinc-100 group-hover:scale-[1.02] transition-all">
                    Leggi l'analisi <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* FASE 8.4 — Sezione Compagnie Partner spostata su /compagnie (link via footer) */}

        {/* ===== URGENCY / DARK SECTION ===== */}
        <section className="relative py-24 md:py-32 bg-zinc-950 text-white overflow-hidden" data-testid="urgency-section">
          <div className="absolute inset-0 grain-texture opacity-[0.04]" />
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#D97B58]/10 blur-[150px]" />
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97B58] mb-4">Il problema</p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-tight">
                  Stai pagando troppo<br />per coperture che<br /><span className="text-[#D97B58]">non ti proteggono.</span>
                </h2>
                <p className="text-lg text-white/50 leading-relaxed mb-8">
                  Il prezzo piu' basso nasconde esclusioni, franchigie nascoste e massimali inadeguati.
                  Lo scopri solo quando serve davvero. Noi leggiamo le clausole per te.
                </p>
                <div className="space-y-4">
                  {[
                    "Il 43% delle polizze ha esclusioni critiche non dichiarate",
                    "Il massimale medio e' sotto la soglia di rischio reale",
                    "Solo il 12% degli italiani ha una polizza vita adeguata",
                  ].map((t, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <AlertTriangle className="w-4 h-4 text-[#D97B58] mt-0.5 shrink-0" />
                      <p className="text-sm text-white/70">{t}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="aspect-[4/3] rounded-3xl overflow-hidden border border-white/10">
                  <img src="https://images.unsplash.com/photo-1726649339367-c2577a28881b?w=800&q=80" alt="Rischio assicurativo" className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 to-transparent" />
                </div>
                <div className="absolute -bottom-6 -left-6 glass-dark rounded-2xl p-5 border border-white/10 max-w-xs">
                  <p className="text-2xl font-heading font-bold text-white">73%</p>
                  <p className="text-xs text-white/50">degli italiani non ha copertura adeguata</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== PROCESS ===== */}
        <section id="processo" className="py-24 md:py-32" data-testid="process-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="text-center mb-16">
              <motion.p variants={fade} className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97B58] mb-4">Come Funziona</motion.p>
              <motion.h2 variants={fade} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 mb-4">
                Tre passi. <span className="text-zinc-400">Protezione reale.</span>
              </motion.h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {STEPS.map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.6 }}
                  className="relative group p-8 rounded-2xl bg-white border border-zinc-200/80 hover:border-[#163321]/20 hover:-translate-y-1 hover:shadow-xl transition-all duration-300" data-testid={`process-step-${i}`}>
                  <span className="font-heading text-6xl font-bold text-zinc-100 group-hover:text-[#163321]/10 transition-colors">{s.num}</span>
                  <div className="w-12 h-12 rounded-xl bg-[#163321]/5 flex items-center justify-center mt-4 mb-5 group-hover:bg-[#163321]/10 transition-colors">
                    <s.icon className="w-6 h-6 text-[#163321]" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-zinc-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link to="/scopri-polizze" className="inline-flex items-center gap-2 px-8 h-14 bg-[#163321] text-white text-sm font-semibold rounded-full hover:bg-[#1d4a2e] hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-[#163321]/20" data-testid="process-cta-btn">
                Inizia ora — e' gratis <Sparkles className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* ===== DIFFERENTIATION ===== */}
        <section id="chi-siamo" className="py-24 md:py-32 bg-zinc-50" data-testid="differentiation-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97B58] mb-4">Perche' Noi</p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 mb-6 leading-tight">
                  Non un comparatore.<br /><span className="text-zinc-400">Un partner per la vita.</span>
                </h2>
                <p className="text-lg text-zinc-500 leading-relaxed mb-8">
                  Facile.it ti da' un prezzo. Noi ti diamo una strategia.
                  Ogni polizza che proponiamo e' pensata per la tua situazione reale, non per quella di un profilo generico.
                </p>
                <div className="rounded-2xl overflow-hidden">
                  <img src="https://images.unsplash.com/photo-1714974528703-e5ad41abc259?w=800&q=80" alt="Consulente" className="w-full h-64 object-cover" loading="lazy" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {DIFFS.map((d, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-zinc-200/80 p-6 hover:-translate-y-1 hover:shadow-lg hover:border-[#163321]/10 transition-all duration-300" data-testid={`diff-card-${i}`}>
                    <div className="w-10 h-10 rounded-xl bg-[#163321]/5 flex items-center justify-center mb-4">
                      <d.icon className="w-5 h-5 text-[#163321]" />
                    </div>
                    <h3 className="font-heading text-base font-semibold text-zinc-900 mb-2">{d.title}</h3>
                    <p className="text-sm text-zinc-500 leading-relaxed">{d.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== TEAM TIMELINE (storia) ===== */}
        <TeamTimeline />

        {/* ===== TEAM ===== */}
        <section id="team" className="py-24 md:py-32" data-testid="team-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97B58] mb-4">Il Nostro Team</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 mb-4">
                Persone vere. <span className="text-zinc-400">Non chatbot.</span>
              </h2>
              <p className="text-lg text-zinc-500 max-w-xl mx-auto">
                Ogni membro del team e' un professionista del settore assicurativo con anni di esperienza. Parli con chi decide.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10 max-w-2xl mx-auto">
              {[
                { name: "Ciro Colucci", role: "Founder & CEO", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DSC08407-Migliorato-NR-600x750.jpg" },
                { name: "Christian Colucci", role: "Chief Executive Officer", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/07/WhatsApp-Image-2025-07-11-at-18.11.52-1200x1500.jpeg" },
              ].map((m, i) => (
                <div key={i} className="bg-white rounded-2xl border border-zinc-200/80 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300" data-testid={`team-leader-${i}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <TeamAvatar
                      name={m.name}
                      role={m.role}
                      src={m.img}
                      imgClassName="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 text-center">
                    <h3 className="font-heading text-base font-semibold text-zinc-900">{m.name}</h3>
                    <p className="text-xs text-[#D97B58] mt-1 font-medium">{m.role}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[
                { name: "Alessandro I. Salvemini", role: "Team Manager", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DSC08496-Migliorato-NR-600x750.jpg" },
                { name: "Mario Algeri", role: "Resp. Amm.", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DSC08422-Migliorato-NR-600x750.jpg" },
                { name: "Walter Di Bello", role: "Resp. Motor", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DSC08440-Migliorato-NR-600x750.jpg" },
                { name: "Mario Mazzei", role: "Assuntore", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DSC08446-Migliorato-NR-600x750.jpg" },
                { name: "Giuseppe Buonaiuto", role: "Assuntore Motor", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DSC08456-Migliorato-NR-600x750.jpg" },
                { name: "Giulio Buongiovanni", role: "Assuntore", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DSC08474-Migliorato-NR-600x750.jpg" },
                { name: "Carmine Crisci", role: "Vita e Danni", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DSC08480-Migliorato-NR-600x750.jpg" },
                { name: "Pasqualina Esposito", role: "Convenzioni", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DSC08527-Migliorato-NR-600x750.jpg" },
                { name: "Pasquale di Gennaro", role: "Vita e Banca", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DSC08506-Migliorato-NR-600x750.jpg" },
                { name: "Monica Sasso", role: "Ramo Danni", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DSC08516-Migliorato-NR-600x750.jpg" },
                { name: "Maria Rosaria Iannelli", role: "Resp. Ufficio Sinistri", img: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DSC08544-Migliorato-NR-600x750.jpg" },
              ].map((m, i) => (
                <div key={i} className="bg-white rounded-2xl border border-zinc-200/80 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid={`team-member-${i}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <TeamAvatar
                      name={m.name}
                      role={m.role}
                      src={m.img}
                      imgClassName="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3 text-center">
                    <h3 className="font-heading text-xs font-semibold text-zinc-900 leading-tight">{m.name}</h3>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{m.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== GOOGLE REVIEWS CAROUSEL (recensioni reali sincronizzate) ===== */}
        <GoogleReviewsCarousel limit={9} />

        {/* ===== INSTAGRAM REDAZIONE STRIP ===== */}
        <InstagramRedazioneStrip
          variant="dark"
          title="News assicurative: video su Instagram, caroselli da salvare."
          subtitle="Ogni settimana la redazione di Colucci Assicuratori trasforma le notizie piu' importanti in video-pillole da 60 secondi e caroselli con le informazioni chiave. Segui @colucci_assicuratori e non perdere nulla."
          limit={4}
        />

        {/* ===== HUMAN TOUCH ===== */}
        <section className="relative py-24 md:py-32 bg-[#163321] text-white overflow-hidden" data-testid="human-touch-section">
          <div className="absolute inset-0 grain-texture opacity-[0.03]" />
          <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
            <img src="/logo_bianco.png" alt="Colucci" className="h-10 sm:h-12 object-contain mx-auto mb-8 opacity-60" />
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              Parlerai con un consulente reale.<br />
              <span className="text-white/40">Mai con un sistema automatico.</span>
            </h2>
            <p className="text-lg text-white/50 leading-relaxed mb-10 max-w-xl mx-auto">
              Ogni richiesta viene analizzata personalmente. Non lasciamo che un algoritmo decida della tua protezione.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/consulenza" className="inline-flex items-center justify-center gap-2 px-8 h-14 bg-white text-[#163321] text-sm font-semibold rounded-full hover:bg-zinc-100 transition-all" data-testid="human-touch-cta-btn">
                Prenota consulenza <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:0817611000" className="inline-flex items-center justify-center gap-2 px-8 h-14 border border-white/20 text-white text-sm font-semibold rounded-full hover:bg-white/10 transition-all" data-testid="human-touch-phone-btn">
                <Phone className="w-4 h-4" /> 081 761 1000
              </a>
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section id="faq" className="py-24 md:py-32" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97B58] mb-4">FAQ</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 mb-4">
                Domande frequenti.
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="bg-white rounded-2xl border border-zinc-200/80 px-6 data-[state=open]:shadow-lg data-[state=open]:border-[#163321]/10 transition-all" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-heading text-base font-semibold text-zinc-900 py-5 hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-zinc-500 leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ===== PARTNER ===== */}
        <section className="py-16 md:py-20 bg-zinc-50" data-testid="partner-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 text-center mb-10">Partner Assicurativi</p>
            <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12 md:gap-16">
              {[
                { name: "Dual", src: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/DUAL-LOGO_3_edited.png" },
                { name: "AIG", src: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/aiglogopng.png" },
                { name: "Reale Mutua", src: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/2560px-Reale_Mutua_Assicurazioni_logo.svg.png" },
                { name: "Groupama", src: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/Risorsa-2-scaled.png" },
                { name: "Net Insurance", src: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/logo150.png" },
                { name: "Lloyd's", src: "https://www.colucciassicuratori.com/wp-content/uploads/2025/05/Lloyds-logo_edited.jpg" },
              ].map((p, i) => (
                <div key={i} className="grayscale hover:grayscale-0 opacity-40 hover:opacity-100 transition-all duration-300" data-testid={`partner-logo-${i}`}>
                  <img src={p.src} alt={p.name} className="h-8 md:h-10 w-auto object-contain" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FINAL CTA ===== */}
        <section className="py-24 md:py-32" data-testid="final-cta-section">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 mb-4">
              Pronto a proteggere cio' che conta?
            </h2>
            <p className="text-lg text-zinc-500 mb-10 max-w-xl mx-auto">
              Non rimandare. Il momento migliore per assicurarsi e' prima che succeda qualcosa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/consulenza" className="inline-flex items-center justify-center gap-2 px-10 h-14 bg-[#163321] text-white text-sm font-semibold rounded-full hover:bg-[#1d4a2e] hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-[#163321]/20" data-testid="final-cta-btn">
                Prenota la tua consulenza <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:0817611000" className="inline-flex items-center justify-center gap-2 px-8 h-14 border border-zinc-200 text-zinc-700 text-sm font-semibold rounded-full hover:bg-zinc-50 transition-all">
                <Phone className="w-4 h-4" /> 081 761 1000
              </a>
            </div>
          </div>
        </section>

        <Footer />

        {/* ===== REVIEW MODAL ===== */}
        {showReview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="review-modal">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowReview(false)} />
            <div className="relative bg-white rounded-2xl max-w-md w-full p-6 sm:p-8 shadow-2xl animate-fade-in-up">
              {reviewSent ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-green-50 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-zinc-900 mb-2">Grazie!</h3>
                  <p className="text-sm text-zinc-500 mb-6">La tua recensione anonima e' stata inviata al nostro team.</p>
                  <button onClick={() => setShowReview(false)} className="px-6 py-2.5 bg-[#163321] text-white text-sm font-semibold rounded-full hover:bg-[#1d4a2e] transition-all">Chiudi</button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#D97B58]/10 flex items-center justify-center"><Star className="w-5 h-5 text-[#D97B58]" /></div>
                      <div>
                        <h3 className="font-heading text-base font-bold text-zinc-900">Recensione Anonima</h3>
                        <p className="text-xs text-zinc-500">Nessun dato personale verra' raccolto</p>
                      </div>
                    </div>
                    <button onClick={() => setShowReview(false)} className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400"><span className="text-lg leading-none">&times;</span></button>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <label className="text-xs font-medium text-zinc-600 block mb-2">Voto</label>
                      <div className="flex gap-1" data-testid="review-stars">
                        {[1,2,3,4,5].map(n => (
                          <button key={n} onClick={() => setReviewData(p => ({ ...p, voto: n }))}
                            className={`w-10 h-10 rounded-xl border-2 flex items-center justify-center transition-all ${n <= reviewData.voto ? 'border-[#D97B58] bg-[#D97B58]/10' : 'border-zinc-200 hover:border-zinc-300'}`}
                            data-testid={`star-${n}`}>
                            <Star className={`w-5 h-5 ${n <= reviewData.voto ? 'text-[#D97B58] fill-[#D97B58]' : 'text-zinc-300'}`} />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-zinc-600 block mb-2">Servizio utilizzato (opzionale)</label>
                      <select value={reviewData.servizio} onChange={e => setReviewData(p => ({ ...p, servizio: e.target.value }))}
                        className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-[#163321]/20" data-testid="review-servizio">
                        <option value="">Seleziona...</option>
                        <option value="auto">RC Auto</option>
                        <option value="casa">Casa</option>
                        <option value="vita">Vita</option>
                        <option value="sanitario">RC Professionale Sanitario</option>
                        <option value="aziende">Aziende</option>
                        <option value="consulenza">Consulenza</option>
                        <option value="altro">Altro</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-zinc-600 block mb-2">La tua esperienza *</label>
                      <textarea value={reviewData.testo} onChange={e => setReviewData(p => ({ ...p, testo: e.target.value }))}
                        placeholder="Racconta la tua esperienza con Colucci Assicuratori..."
                        className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#163321]/20 min-h-[120px] resize-none"
                        data-testid="review-testo" />
                    </div>

                    <button onClick={submitReview} disabled={!reviewData.testo.trim() || reviewSending}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#163321] text-white text-sm font-semibold rounded-full hover:bg-[#1d4a2e] disabled:opacity-40 transition-all shadow-lg shadow-[#163321]/20"
                      data-testid="review-submit-btn">
                      {reviewSending ? "Invio..." : <><Send className="w-4 h-4" /> Invia Recensione</>}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </>
  );
}
